import { useCallback, useEffect, useRef } from "react";

export default function usePolling(refresh, intervalMs) {
  // Keep the latest callback without changing requestRefresh's identity or
  // restarting the polling effect on each render. Ref updates don't re-render.
  const refreshRef = useRef(refresh);
  const active = useRef(null);
  const pending = useRef(false);
  const mounted = useRef(false);
  refreshRef.current = refresh;

  const requestRefresh = useCallback(() => {
    // Requests during an active refresh activates one follow-up refresh.
    // Create a cycle when none is running, so refreshes never overlap.
    pending.current = true;
    if (!active.current) {
      // Defer the loop until its promise has been assigned to active.current.
      // The callback must check isActive() before applying async results.
      active.current = Promise.resolve()
        .then(async () => {
          while (pending.current && mounted.current) {
            pending.current = false;
            await refreshRef.current(() => mounted.current); // isActive()
          }
        })
        .finally(() => {
          active.current = null;
        });
    }
    return active.current;
  }, []);

  useEffect(() => {
    mounted.current = true;
    let stopped = false;
    let timer;
    const poll = async () => {
      try {
        await requestRefresh();
      } finally {
        // A null interval retains serialized manual refreshes without a timer.
        if (!stopped && intervalMs !== null)
          timer = setTimeout(poll, intervalMs);
      }
    };
    poll(); // Refresh immediately, then poll
    return () => {
      stopped = true; // Mark the polling loop as stopped to prevent further timers.
      mounted.current = false; // Stops the polling loop; isActive() returns false.
      pending.current = false; // Discard any pending refresh requests.
      clearTimeout(timer);
    };
  }, [intervalMs, requestRefresh]);

  return requestRefresh;
}
